import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, ProtectedRoute } from './lib/auth';
import { useIsStaff } from './hooks/useMe';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ContactsPage from './pages/ContactsPage';
import ContactDetailPage from './pages/ContactDetailPage';
import ReportersPage from './pages/ReportersPage';
import ReporterDetailPage from './pages/ReporterDetailPage';
import PublicationsPage from './pages/PublicationsPage';
import PublicationDetailPage from './pages/PublicationDetailPage';
import PressListsPage from './pages/PressListsPage';
import PressListDetailPage from './pages/PressListDetailPage';
import OutreachPage from './pages/OutreachPage';
import ComposePage from './pages/ComposePage';
import SendQueuePage from './pages/SendQueuePage';
import OutreachDashboardPage from './pages/OutreachDashboardPage';
import AdminPage from './pages/AdminPage';
import ClientDetailPage from './pages/ClientDetailPage';
import ClientsPage from './pages/ClientsPage';
import HitsPage from './pages/HitsPage';
import DashboardPage from './pages/DashboardPage';
import OnboardingPage from './pages/OnboardingPage';
import TeamSettingsPage from './pages/TeamSettingsPage';
import LandingPage from './pages/LandingPage';
import FeaturesPage from './pages/FeaturesPage';
import PricingPage from './pages/PricingPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import './index.css';

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [Sentry.replayIntegration()],
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
});

// Gate the /admin route on platform-staff status (STAFF_EMAILS allowlist).
// Non-staff users are redirected to the dashboard. The API independently
// enforces staff access via requireStaff on every /api/admin/* route.
function StaffRoute({ children }: { children: React.ReactNode }) {
  const isStaff = useIsStaff();
  if (!isStaff) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Auth routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />

            {/* Public marketing routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />

            {/* Protected app routes — pathless layout, renders when any child matches */}
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/contacts" element={<ContactsPage />} />
              <Route path="/contacts/:id" element={<ContactDetailPage />} />
              <Route path="/reporters" element={<ReportersPage />} />
              <Route path="/reporters/:id" element={<ReporterDetailPage />} />
              <Route path="/publications" element={<PublicationsPage />} />
              <Route path="/publications/:id" element={<PublicationDetailPage />} />
              <Route path="/press-lists" element={<PressListsPage />} />
              <Route path="/press-lists/:id" element={<PressListDetailPage />} />
              <Route path="/outreach" element={<OutreachPage />} />
              <Route path="/compose" element={<ComposePage />} />
              <Route path="/send-queue" element={<SendQueuePage />} />
              <Route path="/outreach-history" element={<OutreachDashboardPage />} />
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/clients/:id" element={<ClientDetailPage />} />
              <Route path="/hits" element={<HitsPage />} />
              <Route
                path="/admin"
                element={
                  <StaffRoute>
                    <AdminPage />
                  </StaffRoute>
                }
              />
              <Route path="/settings" element={<TeamSettingsPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);
